import { HeadsetIcon } from "lucide-react";
import type React from "react";
import { useI18n } from "../../lib/i18n";

/**
 * Interface representing the result of a single scenario calculation
 */
export interface ScenarioResult {
  scenario: "refuse" | "move-reuse" | "reuse-refurbish" | "new";
  co2Impact: number; // kg CO2e
  cost: number; // euros
}

/**
 * Interface representing the complete calculation results with all scenarios
 */
export interface CalculationResult {
  scenarios: ScenarioResult[];
  savings: {
    co2: number;
    cost: number;
  };
}

/**
 * Props for ResultsTable component
 */
interface ResultsTableProps {
  results: CalculationResult | null;
  onContactClick?: () => void;
}

/**
 * Comparative results table
 * Faithfully reproduces the Excel table design
 * Displays 4 scenarios with CO2 and costs
 */
const ResultsTable: React.FC<ResultsTableProps> = ({
  results,
  onContactClick,
}) => {
  const { t, language } = useI18n();

  if (!results) {
    return null;
  }

  /**
   * Formats numbers according to locale
   */
  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat(language === "fr" ? "fr-FR" : "nl-NL").format(
      Math.round(num),
    );
  };

  const scenario = (key: ScenarioResult["scenario"]) =>
    results.scenarios.find((s) => s.scenario === key);

  const refuse = scenario("refuse");
  const move = scenario("move-reuse");
  const refurbish = scenario("reuse-refurbish");
  const newScenario = scenario("new");

  const savedKg = Math.max(
    0,
    (newScenario?.co2Impact || 0) - (move?.co2Impact || 0),
  );
  const savedKm = savedKg * 4.5;

  return (
    <section className="w-full">
      <div className="tm-bar text-transmoove-white px-4 py-1 text-tm-xl font-semibold">
        {t("results.title")}
      </div>

      <div>
        <div className="grid grid-cols-[22%_30%_14%_17%_17%] border-b border-transmoove-bar text-tm-sm text-transmoove-text">
          <div className="px-3 py-2 font-semibold">{t("results.scenario")}</div>
          <div className="px-3 py-2" />
          <div className="px-3 py-2 text-center font-semibold">
            {t("results.co2Impact")}
          </div>
          <div className="px-3 py-2 text-center font-semibold">
            {t("results.cost")}
          </div>
          <div className="px-3 py-2 text-center font-semibold">
            {t("results.remarks")}
          </div>
        </div>

        <div className="grid grid-cols-[22%_30%_14%_17%_17%] border-b border-transmoove-bar text-tm-base text-transmoove-text">
          <div className="px-3 py-2 text-tm-3xl font-semibold">
            {t("results.scenario.refuse")}
          </div>
          <div className="px-3 py-2">{t("results.desc.refuse")}</div>
          <div className="px-3 py-2 text-center">
            {formatNumber(refuse?.co2Impact || 0)}
          </div>
          <div className="px-3 py-2 text-center">
            {formatNumber(refuse?.cost || 0)} €
          </div>
          <div className="px-3 py-2" />
        </div>

        <div className="grid grid-cols-[22%_30%_14%_17%_17%] border-b border-transmoove-bar text-tm-lg">
          <div className="px-3 py-2 bg-transmoove-bgDark text-white text-tm-3xl italic font-semibold">
            {t("results.scenario.move-reuse")}
          </div>
          <div className="px-3 py-2 bg-transmoove-bgDark text-white italic font-semibold">
            {t("results.desc.move-reuse")}
          </div>
          <div className="px-3 py-2 text-center bg-transmoove-bgDark text-white italic font-semibold">
            {formatNumber(move?.co2Impact || 0)}
          </div>
          <div className="px-3 py-2 text-center bg-transmoove-bgDark text-white italic font-semibold">
            {formatNumber(move?.cost || 0)} €
          </div>
          <div className="px-3 py-2 bg-transmoove-bgDark text-tm-xs text-white italic font-semibold">
            {t("results.remarks.move-reuse")}
          </div>
        </div>

        <div className="grid grid-cols-[22%_30%_14%_17%_17%] border-b border-transmoove-bar text-tm-base text-transmoove-text">
          <div className="px-3 py-2 text-tm-3xl font-semibold">
            {t("results.scenario.reuse-refurbish")}
          </div>
          <div className="px-3 py-2">{t("results.desc.reuse-refurbish")}</div>
          <div className="px-3 py-2 text-center">
            {formatNumber(refurbish?.co2Impact || 0)}
          </div>
          <div className="px-3 py-2 text-center">
            {formatNumber(refurbish?.cost || 0)} €
          </div>
          <div className="px-3 py-2 text-tm-xs">
            {t("results.remarks.reuse-refurbish")}
          </div>
        </div>

        <div className="grid grid-cols-[22%_30%_14%_17%_17%] border-b border-transmoove-bar text-tm-base text-transmoove-text">
          <div className="px-3 py-2 text-tm-3xl font-semibold">
            {t("results.scenario.new")}
          </div>
          <div className="px-3 py-2">{t("results.desc.new")}</div>
          <div className="px-3 py-2 text-center">
            {formatNumber(newScenario?.co2Impact || 0)}
          </div>
          <div className="px-3 py-2 text-center">
            {formatNumber(newScenario?.cost || 0)} €
          </div>
          <div className="px-3 py-2 text-tm-xs">{t("results.remarks.new")}</div>
        </div>
      </div>

      <div className="mt-4 bg-transmoove-bgDark grid gap-0 lg:grid-cols-[60%_40%] items-center">
        {savedKg > 0 ? (
          <div className="text-white px-4 py-3 text-tm-3xl font-semibold">
            <div>
              {t("results.savings.message", {
                co2: formatNumber(savedKg),
                cost: formatNumber(
                  (newScenario?.cost || 0) - (move?.cost || 0),
                ),
                km: formatNumber(savedKm),
              })}
            </div>
          </div>
        ) : (
          <div />
        )}

        <div className="px-4 py-3 flex justify-end">
          <button
            type="button"
            onClick={onContactClick}
            className="bg-white px-6 py-2 tm-font-3xl italic font-semibold text-transmoove-text inline-flex items-center gap-2"
          >
            <HeadsetIcon />
            {t("contact.button")}
          </button>
        </div>
      </div>

      <div className="mt-3 text-tm-2xs leading-tight text-transmoove-text">
        <div className="italic">{t("results.disclaimer.intro")}</div>
        <div className="mt-1 italic">{t("results.disclaimer.star")}</div>
        <div className="mt-1 italic">{t("results.disclaimer.unit")}</div>
        <div className="mt-1 italic">{t("results.disclaimer.doubleStar")}</div>
      </div>
    </section>
  );
};

export default ResultsTable;
